import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import OccupancyHeatmap from '../shared/OccupancyHeatmap';
import ComparisonSelector from '../shared/ComparisonSelector';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { useOccupancyAnalysis } from '../../../hooks/analysis/useOccupancyAnalysis';
import { ComparisonMode, COMPARISON_NOTE } from '../../../utils/analytics/comparison';
import { formatCurrency, formatPercentage } from '../../../utils/formatters';
import { AXIS_PROPS, CHART_INK, SERIES_COLORS } from '../shared/chartTheme';

const OccupancyAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const [comparison, setComparison] = useState<ComparisonMode>('none');
  const data = useOccupancyAnalysis(range, comparison);

  return (
    <div>
      <div className="analysis-controls">
        <DateRangePicker value={range} onChange={setRange} />
        <ComparisonSelector value={comparison} onChange={setComparison} />
      </div>

      <KpiRow>
        <KpiTile
          label="Avg occupancy"
          value={formatPercentage(data.avgPct)}
          delta={data.avgPctDelta}
          note={comparison !== 'none' ? COMPARISON_NOTE[comparison] : `${data.siteCount} sites`}
        />
        <KpiTile label="Peak day" value={data.peak.label} note={data.peak.date ? formatPercentage(data.peak.pct) : ''} />
        <KpiTile label="Weekend avg" value={formatPercentage(data.split.weekend)} note="Fri–Sat nights" />
        <KpiTile label="Weekday avg" value={formatPercentage(data.split.weekday)} note="Sun–Thu nights" />
        <KpiTile
          label="Gap nights"
          value={String(data.gaps.length)}
          note={data.gapLostRevenue !== null ? `≈ ${formatCurrency(data.gapLostRevenue)} at ADR` : 'unsellable 1-night gaps'}
        />
        <KpiTile label="Blackout site-nights" value={String(data.blackoutNights)} note="removed from availability" />
      </KpiRow>

      <ChartPanel
        title="Occupancy rate"
        subtitle="Occupied ÷ available sites per night"
        loading={data.loading}
        error={data.error}
        empty={data.series.length === 0 || data.siteCount === 0}
        table={{
          columns: [
            { key: 'date', label: 'Date' },
            { key: 'occupied', label: 'Occupied', align: 'right' },
            { key: 'available', label: 'Available', align: 'right' },
            { key: 'pct', label: 'Occupancy', align: 'right', format: v => formatPercentage(Number(v)) },
          ],
          rows: data.series,
        }}
      >
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.series} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} domain={[0, 100]} tickFormatter={v => `${v}%`} width={48} />
            <Tooltip formatter={(value: any) => [formatPercentage(Number(value)), 'Occupancy']} />
            <Area
              type="monotone"
              dataKey="pct"
              stroke={SERIES_COLORS[0]}
              strokeWidth={2}
              fill={SERIES_COLORS[0]}
              fillOpacity={0.12}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartPanel>

      <div className="chart-panel-grid">
        <ChartPanel
          title="Arrivals by weekday"
          loading={data.loading}
          error={data.error}
          empty={data.arrivals.every(a => a.value === 0)}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.arrivals} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} width={36} />
              <Tooltip formatter={(value: any) => [value, 'Arrivals']} />
              <Bar dataKey="value" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Departures by weekday"
          loading={data.loading}
          error={data.error}
          empty={data.departures.every(d => d.value === 0)}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.departures} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} width={36} />
              <Tooltip formatter={(value: any) => [value, 'Departures']} />
              <Bar dataKey="value" fill={SERIES_COLORS[1]} radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      <ChartPanel
        title="Site × week heatmap"
        subtitle="Share of each week's nights occupied, per site"
        loading={data.loading}
        error={data.error}
        empty={data.heatmapRows.length === 0}
      >
        <OccupancyHeatmap rows={data.heatmapRows} weekStarts={data.weekStarts} />
      </ChartPanel>
    </div>
  );
};

export default OccupancyAnalysis;
